import PaymentConfirmationModal from './modals/payment-confirmation';
import { DateTime } from 'luxon';
import { Button, Card, Col, Row, Form, InputGroup } from 'react-bootstrap';
import { FaCheck, FaCircleCheck, FaCopy, FaClock, FaQrcode, FaShieldHalved, FaTriangleExclamation, FaMobileScreenButton } from 'react-icons/fa6';
import ClipboardCopy from './clipboard-copy';
import Image from 'next/image';
import { http, layout, messages } from '../helpers';
import { useRouter } from 'next/router';
import React, { useEffect, useState, useRef } from 'react';

export default function Pix({ order, showCountdown, reloadOnConfirm, message, redirect, ignorePaymentCheck }) {
  const router = useRouter();
  const [countdown, setCountdown] = useState();
  const [copied, setCopied] = useState(false);
  const [expired, setExpired] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const timerRef = useRef(null);

  const runCountdown = () => {
    if (!order?.createdAt) return;
    const end = DateTime.fromJSDate(new Date(order.createdAt)).plus({ hours: 1 });
    const now = DateTime.now();
    const remaining = end.diff(now, ['minutes', 'seconds']);
    if (remaining.minutes <= 0 && remaining.seconds <= 0) {
      setCountdown('00:00');
      setExpired(true);
      return;
    }
    setCountdown(remaining.toFormat('mm:ss'));
    timerRef.current = setTimeout(runCountdown, 1000);
  };

  const handlePaymentCheck = () => {
    layout.loading(true);
    http.get(`/orders/${order._id}`, (result) => {
      layout.loading(false);
      if (result && result.status === 'CONFIRMED') {
        messages.clear();
        setModalShow(false);
        messages.success(message ?? 'Pagamento confirmado.', false, true);
        if (reloadOnConfirm) {
          window.location.reload();
        }
        if (redirect) {
          router.push(redirect);
        }
        return;
      }
      if (modalShow) {
        setModalShow(false);
        messages.clear();
        messages.alert('Seu pagamento ainda não foi processado. Por favor, aguarde alguns instantes e tente confirmar novamente.', false, true);
      }
    });
  };

  useEffect(() => {
    if (showCountdown) {
      runCountdown();
    }
    if (!ignorePaymentCheck) {
      handlePaymentCheck();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopySuccess = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  if (!order?.payment || order.status === 'CONFIRMED') return null;

  const qrCodeSrc = order.payment.pix?.qrcode || (order.payment.encodedImage ? `data:image/png;base64,${order.payment.encodedImage}` : null);
  const pixPayload = order.payment.pix?.qrcode_text || order.payment.payload || '';

  return (
    <Card className="border-0 shadow-sm rounded-4 overflow-hidden my-3 bg-gradient">
      <Card.Body className="p-3 p-md-4">
        {expired ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">Este código PIX não é mais válido. Por favor, solicite um novo pagamento.</p>
          </div>
        ) : (
          <Row className="g-4 align-items-center">
            {/* QR Code Column */}
            <Col xs={12} sm={5} className="text-center">
              <div className="bg-white p-3 rounded-4 shadow-sm border d-inline-block position-relative">
                {qrCodeSrc ? (
                  <Image
                    src={qrCodeSrc}
                    alt="QR Code PIX"
                    width={180}
                    height={180}
                    className="img-fluid rounded-2"
                  />
                ) : (
                  <div className="d-flex align-items-center justify-content-center bg-light rounded" style={{ width: 180, height: 180 }}>
                    <FaQrcode className="text-muted fs-1" />
                  </div>
                )}
              </div>
              <div className="mt-2 text-muted small d-flex align-items-center justify-content-center gap-1">
                <FaMobileScreenButton className="text-secondary" />
                <span>Escaneie com o app do seu banco</span>
              </div>
            </Col>

            {/* PIX Copy-Paste & Action Column */}
            <Col xs={12} sm={7}>
              <div className="d-flex flex-column gap-3">
                {showCountdown && (
                  <div className="d-flex align-items-center justify-content-center mb-3">
                    {!expired ? (
                      <div className="d-inline-flex align-items-center gap-2 bg-white px-3 py-2 rounded-pill shadow-sm border">
                        <FaClock className="text-primary fs-5" />
                        <span className="text-muted small fw-medium">Expira em:</span>
                        <span className="fw-bold font-monospace fs-5 text-dark">{countdown || '--:--'}</span>
                      </div>
                    ) : (
                      <div className="d-inline-flex align-items-center gap-2 bg-danger-subtle text-danger px-3 py-2 rounded-pill border border-danger-subtle">
                        <FaTriangleExclamation className="fs-5" />
                        <span className="fw-bold small">Código de Pagamento expirado</span>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <ClipboardCopy value={order.payment.pix ? order.payment.pix.qrcode_text : order.payment.payload} setCopied={setCopied}>
                    <Button className="btn-gradient-primary rounded-pill px-4 py-2 fw-bold border-0 shadow-sm w-100">
                      <FaCopy className="me-2" />
                      Copia e Cola PIX
                    </Button>
                  </ClipboardCopy>
                  {copied && (
                    <div className="mt-2 text-success small fw-bold d-flex align-items-center justify-content-center justify-content-sm-center">
                      <FaCircleCheck className="me-1" /> Código copiado!
                    </div>
                  )}
                </div>
              </div>
            </Col>
            <Col xs={12}>
              <div className="d-flex flex-column gap-3">
                <div className="bg-white p-3 rounded-3 border small text-muted">
                  <div className="fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                    <FaShieldHalved className="text-success" />
                    Como pagar?
                  </div>
                  <ol className="mb-0 ps-3 text-secondary" style={{ fontSize: '0.8rem' }}>
                    <li>Abra o aplicativo do seu banco</li>
                    <li>Escolha a opção <strong>Pix</strong> &gt; <strong>Pix Copia e Cola</strong> ou <strong>Ler QR Code</strong></li>
                    <li>Após pagar, clique em <strong>Confirmar Pagamento</strong>.</li>
                  </ol>
                </div>
                <div className="pt-1">
                  <Button
                    type="button"
                    className="btn-gradient-primary rounded-pill px-4 py-3 fw-bold border-0 fs-5 w-100 shadow-sm d-flex align-items-center justify-content-center gap-2"
                    onClick={() => setModalShow(true)}
                  >
                    <FaCheck className="fs-5" />
                    <span>Confirmar Pagamento</span>
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>

      <PaymentConfirmationModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConfirm={handlePaymentCheck}
        canConfirm={order && !ignorePaymentCheck}
      />
    </Card>
  );
}

